// The Settings modal's saved values. There is no preferences backend: a choice
// lives in localStorage under `wt-set:<name>` and survives a reload. This is a
// leaf module (no imports) so anything may read a setting without pulling the
// modal in.
//
// A saved value is only trusted when it is of the kind the setting holds:
// whatever else the storage has — a broken string, another type, a number out
// of range, a word that is no option — reads as the default.

const setKey = (name: string) => `wt-set:${name.toLowerCase().replace(/\s+/g, '-')}`

/** The saved value of a setting, or `initial` when nothing usable is saved:
 *  nothing, unparsable JSON, or a value of another type than `initial`. */
export function readSetting<T>(name: string, initial: T): T {
  try {
    const raw = window.localStorage.getItem(setKey(name))
    if (raw == null) return initial
    const value: unknown = JSON.parse(raw)
    // `null` is an object to typeof: never a saved choice
    return value !== null && typeof value === typeof initial ? (value as T) : initial
  } catch {
    return initial
  }
}

export function writeSetting<T>(name: string, value: T) {
  try {
    window.localStorage.setItem(setKey(name), JSON.stringify(value))
  } catch {
    // quota / private mode — the setting just does not survive a reload
  }
  announce()
}

/** A whole number within [min, max], or `initial`. */
export function readInt(name: string, initial: number, min: number, max: number): number {
  const value = readSetting<number>(name, initial)
  return Number.isInteger(value) && value >= min && value <= max ? value : initial
}

/** One of `options`, or `initial`. */
export function readChoice<T extends string>(name: string, options: readonly T[], initial: T): T {
  const value = readSetting<string>(name, initial)
  return (options as readonly string[]).includes(value) ? (value as T) : initial
}

/** Forgets the saved values of `names`: each reads as its default again. */
export function clearSettings(names: readonly string[]) {
  try {
    for (const name of names) window.localStorage.removeItem(setKey(name))
  } catch {
    // storage unavailable — nothing was saved either
  }
  announce()
}

/** True when a value is saved under `name`, usable or not. */
export function hasSetting(name: string): boolean {
  try {
    return window.localStorage.getItem(setKey(name)) != null
  } catch {
    return false
  }
}

// ---- who is told when a setting changes ----------------------------------
// The modal's Reset is lit only while something differs from the defaults, so
// it has to hear of every write.
let version = 0
const listeners = new Set<() => void>()

function announce() {
  version += 1
  for (const listener of listeners) listener()
}

export function subscribeSettings(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Changes on every write and every clear (for useSyncExternalStore). */
export const settingsVersion = () => version

// ---- what the toast stack and the wallet alerts read ---------------------
// One place for a setting's name, default and bounds: the modal draws its
// control from these and the reader below trusts nothing outside them.

export const TOAST_POSITION = 'Toast Position'
export const TOAST_POSITIONS = [
  'Top Left',
  'Top Center',
  'Top Right',
  'Bottom Left',
  'Bottom Center',
  'Bottom Right',
] as const
export type ToastPosition = (typeof TOAST_POSITIONS)[number]
/** Where the stack stood before it could be moved (style lab, round 6). */
export const TOAST_POSITION_DEFAULT: ToastPosition = 'Top Center'

/** Where the toast stack stands. */
export const toastPosition = (): ToastPosition =>
  readChoice(TOAST_POSITION, TOAST_POSITIONS, TOAST_POSITION_DEFAULT)

/** How many toasts the stack holds; the next one pushes the oldest out. Six
 *  is what the stack held before it could be set. */
export const MAX_TOASTS = { name: 'Max toasts shown', initial: 6, min: 1, max: 10 } as const
export const toastStackLimit = (): number =>
  readInt(MAX_TOASTS.name, MAX_TOASTS.initial, MAX_TOASTS.min, MAX_TOASTS.max)

/** How long a wallet alert stays, in seconds; three is what it stayed before
 *  it could be set. A trade's own toasts keep their own times. */
export const TOAST_DURATION = { name: 'Toast duration', initial: 3, min: 1, max: 10 } as const
export const walletAlertDurationMs = (): number =>
  readInt(TOAST_DURATION.name, TOAST_DURATION.initial, TOAST_DURATION.min, TOAST_DURATION.max) *
  1000

export const MUTE_WHILE_INACTIVE = 'Mute while inactive'
/** True when wallet alerts are to wait: the setting is on and nobody is
 *  looking at the terminal (its tab is hidden, or its window has no focus). */
export const mutedWhileInactive = (): boolean =>
  readSetting(MUTE_WHILE_INACTIVE, false) &&
  (document.visibilityState === 'hidden' || !document.hasFocus())

export const DISPLAY_NOTIFICATIONS = 'Display notifications'
/** True when a wallet alert's toast is not to be shown now: the account turned
 *  them off, or asked for quiet while it is away. The alert is in the inbox
 *  either way — only its toast is held back. */
export const walletAlertToastsOff = (): boolean =>
  !readSetting(DISPLAY_NOTIFICATIONS, true) || mutedWhileInactive()
